<template>
	<div v-if="loaded">
		<header class="page-title">
			<h2 class="modal-title">{{ selectedProperty.id === null ? 'New Property' : 'Edit Property' }}</h2>
			<div class="ml-auto" v-if="!userRoles.includes('users.translator')">
				<a v-if="!selectedProperty.hasOwnProperty('id')" @click.prevent="createProperty" class="btn btn-md btn-success" href="#"><i class="fas fa-plus"></i> Create Property</a>
				<a v-else @click.prevent="saveProperty" class="btn btn-md btn-success" href="#"><i class="fas fa-plus"></i> Save Property</a>
			</div>
		</header>
		<div class="card">
			<header class="card-header language-list">
				<lang-selector></lang-selector>
			</header>
			<div class="card-body">
				<div class="form-group">
					<label for="property-name">Name</label>
					<input id="property-name" type="text" v-model="selectedProperty[clickedLang].name" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
					<p class="input-error" v-if="errors.errors[errorNameCurrentLanguage]">{{ errors.errors[errorNameCurrentLanguage] }}</p>
				</div>
				<div class="form-group">
					<label for="property-description">Description</label>
					<textarea class="textarea-small" id="property-description" v-model="selectedProperty[clickedLang].description" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale"></textarea>
					<p class="input-error" v-if="errors.errors[errorDescriptionCurrentLanguage]">{{ errors.errors[errorDescriptionCurrentLanguage] }}</p>
				</div>
				<div class="form-group">
					<label for="property-context-param">Value Context
						Example: "Available on up to %s devices."
						<a href="https://www.php.net/manual/en/function.printf.php">More example</a>
					</label>
					<textarea class="textarea-small" id="property-context-param" v-model="selectedProperty.value_context[clickedLang].text_value" :disabled="!userRoles.includes('users.developer') && !userRoles.includes('articles.editor')"></textarea>
				</div>
				<div class="form-group" v-if="selectedProperty.type === 'checkbox' && parseInt(selectedProperty.mode) === 2">
					<label for="property-text-true">Text for true (checkbox with text mode)</label>
					<input class="textarea-small" id="property-text-true" v-model="selectedProperty[clickedLang].text_for_true" :disabled="!userRoles.includes('users.developer') && !userRoles.includes('articles.editor')">
				</div>
				<div class="form-group" v-if="selectedProperty.type === 'checkbox' && parseInt(selectedProperty.mode) === 2">
					<label for="property-text-false">Text for false (checkbox with text mode)</label>
					<input class="textarea-small" id="property-text-false" v-model="selectedProperty[clickedLang].text_for_false" :disabled="!userRoles.includes('users.developer') && !userRoles.includes('articles.editor')">
				</div>
			</div>
		</div>
		<div class="card" v-if="!userRoles.includes('users.translator')">
			<header class="card-header">
				<h3 class="card-title">General settings</h3>
			</header>
			<div class="card-body">
				<div class="form-group">
					<label>Type</label>
					<div class="checkboxes-inline">
						<label class="label-checkbox">
							<input name="property-type" type="radio" v-model="selectedProperty.type" value="combobox"> Multiselect Combobox
						</label>
						<label class="label-checkbox">
							<input name="property-type" type="radio" v-model="selectedProperty.type" value="number"> Number
						</label>
						<label class="label-checkbox">
							<input name="property-type" type="radio" v-model="selectedProperty.type" value="checkbox"> Checkbox
						</label>
						<p class="input-error" v-if="errors.errors.type">{{ errors.errors.type }}</p>
					</div>
				</div>
				<div class="form-group">
					<label for="property-group">Group</label>
					<select :disabled="selectedProperty.id === null" id="property-group" v-model="selectedProperty.pp_group_id">
						<option :value="group.id" v-for="group in groups">{{ group.name }}</option>
					</select>
					<p class="input-error" v-if="errors.errors.pp_group_id">{{ errors.errors.pp_group_id }}</p>
				</div>
				<div class="form-group">
					<label for="icon-class">Icon Class</label>
					<input id="icon-class" type="text" v-model="selectedProperty.icon_class" :disabled="!userRoles.includes('users.developer')">
				</div>
				<div class="flex box">
					<div class="form-group six-lg" v-if="selectedProperty.type !== 'checkbox'">
						<label for="property-scoring-rule-number">Scoring Rule</label>
						<select id="property-scoring-rule-number" v-model="selectedProperty.scoring_rule">
							<option value="none">None</option>
							<option value="more-is-better">More is Better</option>
							<option value="less-is-better">Less is Better</option>
						</select>
					</div>
					<div class="form-group six-lg" v-else>
						<label for="property-scoring-rule">Scoring Rule</label>
						<select id="property-scoring-rule" v-model="selectedProperty.scoring_rule">
							<option value="none">None</option>
							<option value="checked-is-better">Checked is Better</option>
							<option value="unchecked-is-better">Unchecked is Better</option>
						</select>
					</div>
					<div class="form-group six-lg">
						<label for="property-weight">Default Weight</label>
						<input id="property-weight" type="number" v-model="selectedProperty.default_weight">
					</div>
				</div>
				<div class="form-groups-inline mt-2" v-if="!userRoles.includes('users.translator')">
					<div class="form-group">
						<label class="label-checkbox">
							<input type="checkbox" v-model="selectedProperty.hidden">
							<span>Hidden Property</span>
						</label>
					</div>
					<div class="form-group">
						<label class="label-checkbox">
							<input type="checkbox" v-model="selectedProperty.visible_on_provider_portal">
							<span>Property Visible on Provider Portal</span>
						</label>
					</div>
				</div>
			</div>
		</div>
		<div class="card" v-if="!userRoles.includes('users.translator')">
			<header class="card-header">
				<h3 class="card-title">Type specific settings</h3>
			</header>
			<div class="card-body">
				<!-- IF Type="number" -->
				<div class="flex box start" v-if="selectedProperty.type === 'number' && !userRoles.includes('users.translator')">
					<div class="form-group three-lg mb-0">
						<label for="property-min">Minimum Value</label>
						<input id="property-min" type="number" v-model="selectedProperty.min_value">
						<p class="input-error" v-if="errors.errors.min_value">{{ errors.errors.min_value }}</p>
					</div>
					<div class="form-group three-lg mb-0">
						<label for="property-max">Maximum Value</label>
						<input id="property-max" type="number" v-model="selectedProperty.max_value">
						<p class="input-error" v-if="errors.errors.max_value">{{ errors.errors.max_value }}</p>
					</div>
					<div class="form-group three-lg mb-0">
						<label for="property-decimals">Decimals</label>
						<input id="property-decimals" type="number" v-model="selectedProperty.decimals">
						<p class="input-error" v-if="errors.errors.decimals">{{ errors.errors.decimals }}</p>
					</div>
					<div class="form-group three-lg">
						<label for="property-unit">Unit</label>
						<select id="property-unit" v-model="selectedProperty.unit">
							<option :value="null">No unit</option>
							<option :value="unit" v-for="unit in units">{{ unit }}</option>
						</select>
						<p class="input-error" v-if="errors.errors.unit">{{ errors.errors.unit }}</p>
					</div>
				</div>
				<div class="flex box center" v-else-if="selectedProperty.type === 'checkbox' && !userRoles.includes('users.translator')">
					<div class="form-group four-lg">
						<label for="property-score">Score</label>
						<input disabled id="property-score" type="number" v-model="selectedProperty.score">
					</div>
					<div class="form-group four-lg">
						<label class="label-checkbox">
							<input type="checkbox" v-model="selectedProperty.default_value">
							<span>Default checked</span>
						</label>
					</div>
					<div class="form-group four-lg" v-if="selectedProperty.type === 'checkbox'">
						<label>Mode</label>
						<div class="checkboxes-inline">
							<label class="label-checkbox">
								<input name="property-mode" type="radio" v-model="selectedProperty.mode" value="1"> Default
							</label>
							<label class="label-checkbox">
								<input name="property-mode" type="radio" v-model="selectedProperty.mode" value="2"> With text
							</label>
						</div>
					</div>
				</div>
				<div v-else-if="selectedProperty.type === 'combobox' && !userRoles.includes('users.translator')">
					<div class="text-right mb-2">
						<a @click.prevent="showModal(null)" class="btn btn-md btn-success" href="#"><i class="fas fa-plus"></i> Add new value</a>
					</div>
					<div class="table-responsive">
						<table>
							<thead>
								<tr>
									<th>Title</th>
									<th>Value</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tr v-for="(param, index) in selectedProperty.params">
								<td>{{ param['en'].text_value }}</td>
								<td>{{ param.value }}</td>
								<td class="nowrap">
									<a @click.prevent="showModal(param)" class="btn btn-icon btn-primary" title="Edit"><i class="fas fa-pencil-alt"></i></a>
									<a @click.prevent="deleteParam(index)" class="btn btn-icon btn-danger" title="Delete" v-if="!userRoles.includes('users.translator')"><i class="fas fa-trash-alt"></i></a>
								</td>
							</tr>
						</table>
					</div>
					<div class="modal-bg" v-if="translationModal">
						<div class="modal">
							<header class="modal-header">
								<h4 class="modal-title">Edit Translations</h4>
								<a @click.prevent="hideModal" class="modal-close" title="Close modal"><i class="fas fa-times"></i></a>
							</header>
							<section class="modal-body">
								<div class="card">
									<header class="card-header">
										<h3 class="card-title">General</h3>
									</header>
									<div class="card-body">
										<div class="form-group">
											<label for="param-key">Title</label>
											<input id="param-key" type="text" v-model="selectedParam['en'].text_value" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
										</div>
										<div class="form-group">
											<label for="param-value">Numeric value</label>
											<input id="param-value" type="text" v-model="selectedParam.value" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
										</div>
									</div>
								</div>
								<div class="card mb-0">
									<header class="card-header language-list">
										<lang-selector></lang-selector>
									</header>
									<div class="card-body">
										<div class="form-group">
											<label for="param-translation-text-value">Text Value</label>
											<input id="param-translation-text-value" type="text" v-model="selectedParam[clickedLang].text_value" :disabled="userRoles.includes('users.translator') && clickedLang === defaultLocale">
										</div>
									</div>
								</div>
							</section>
							<footer class="modal-footer">
								<a @click.prevent="saveParam" class="btn btn-md btn-success" href="#"><i class="fas fa-check"></i> Confirm</a>
							</footer>
						</div>
					</div>
				</div>
			</div>
		</div>
		<loader></loader>
	</div>
</template>

<script>
import Errors from "../../../Utils/Errors";
import {eventBus} from "../../../app";

export default {
	name: "ProviderTypePropertyEditor",
	props: {
		property: {
			type: Object
		},
		selectedGroup: {
			required: true,
			type: Object
		},
		groups: {
			required: true,
			type: Array
		}
	},
	data() {
		return {
			clickedLang: this.$defaultLanguage.locale,
			defaultLocale: this.$defaultLanguage.locale,

			selectedProperty: this.property ? this.property : {
				name: null,
				type: null,
				default_value: null,
				default_weight: null,
				pp_group_id: this.selectedGroup.id,
				min_value: null,
				max_value: null,
				score: null,
				scoring_rule: null,
				icon_class: null,
				hidden: false,
				visible_on_provider_portal: false,
				params: [],
				value_context: {
					en: {
						text_value: null
					}
				},
				translations: []
			},

			selectedParam: {
				value: null,
				en: {
					text_value: null
				}
			},
			isParamUpdate: false,

			units: {
				'units.mbps': 'Mbps',
				'units.MB': 'MB',
				'units.GB': 'GB',
				'units.ms': 'ms',
				'units.pcs': 'pcs',
				'units.days': 'days',
				'units.countries': 'countries',
				'units.stars': 'stars',
			},
			userRoles: this.$currentUserRoles,

			translationModal: false,
			errors: new Errors(),
			loaded: false
		}
	},
	mounted() {
		let vm = this;
		// When setActiveStatus method ran, change selected data in form fields
		/* From LangSelector */
		eventBus.$on('setActiveStatus', function (clickedLang) {
			vm.clickedLang = clickedLang;
		});

		if (!vm.selectedProperty.hasOwnProperty('id')) {
			for (let i = 0; i < vm.$availableLanguages.length; i++) {
				vm.selectedProperty[vm.$availableLanguages[i].locale] = {
					name: null,
					description: null,
					text_for_true: null,
					text_for_false: null
				};

				vm.selectedProperty.translations[vm.$availableLanguages[i].locale] = {
					name: null,
					description: null,
				};

				vm.selectedProperty.value_context[vm.$availableLanguages[i].locale] = {
					text_value: null,
				};
			}
		}

		for (let i = 0; i < vm.$availableLanguages.length; i++) {
			if (vm.selectedProperty.translations[vm.$availableLanguages[i].locale]) {
				vm.selectedProperty[vm.$availableLanguages[i].locale] = {
					name: vm.selectedProperty.translations[vm.$availableLanguages[i].locale].name,
					description: vm.selectedProperty.translations[vm.$availableLanguages[i].locale].description,
					text_for_true: vm.selectedProperty.translations[vm.$availableLanguages[i].locale].text_for_true,
					text_for_false: vm.selectedProperty.translations[vm.$availableLanguages[i].locale].text_for_false,
				};
			} else {
				vm.selectedProperty[vm.$availableLanguages[i].locale] = {
					name: null,
					description: null,
					text_for_true: null,
					text_for_false: null
				};
			}

			for (let j = 0; j < vm.selectedProperty.params.length; j++) {
				if (vm.selectedProperty.params[j].translations[vm.$availableLanguages[i].locale]) {
					vm.selectedProperty.params[j][vm.$availableLanguages[i].locale] = {
						text_value: vm.selectedProperty.params[j].translations[vm.$availableLanguages[i].locale].text_value,
					}
				} else {
					vm.selectedProperty.params[j][vm.$availableLanguages[i].locale] = {
						text_value: null,
					}
				}
			}
			vm.selectedParam[vm.$availableLanguages[i].locale] = {
				text_value: null,
			}
		}

		vm.loaded = true;
	},
	methods: {
		generateSelectedPropertyData: function () {
			let vm = this;
			for (let i = 0; i < vm.$availableLanguages.length; i++) {
				if (vm.selectedProperty[vm.$availableLanguages[i].locale] && (vm.selectedProperty[vm.$availableLanguages[i].locale].name || vm.selectedProperty[vm.$availableLanguages[i].locale].description)) {
					if (vm.$availableLanguages[i].locale === vm.$defaultLanguage.locale) {
						vm.selectedProperty.name = vm.selectedProperty[vm.$availableLanguages[i].locale].name;
						vm.selectedProperty.description = vm.selectedProperty[vm.$availableLanguages[i].locale].description;
					} // if
					vm.selectedProperty.translations[vm.$availableLanguages[i].locale] = {
						name: vm.selectedProperty[vm.$availableLanguages[i].locale].name,
						description: vm.selectedProperty[vm.$availableLanguages[i].locale].description,
						text_for_true: vm.selectedProperty[vm.$availableLanguages[i].locale].text_for_true,
						text_for_false: vm.selectedProperty[vm.$availableLanguages[i].locale].text_for_false,
					}
				} else {
					if (vm.$availableLanguages[i].locale !== vm.$defaultLanguage.locale) {
						vm.selectedProperty.translations[vm.$availableLanguages[i].locale] = null;
					}
				}
			}
		},
		createProperty: function () {
			let vm = this;

			eventBus.$emit('onLoaderStart');
			vm.generateSelectedPropertyData();

			axios.post('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.selectedGroup.provider_type_id + '/groups/' + vm.selectedGroup.id + '/properties', vm.selectedProperty)
					.then(function (response) {
						vm.$toastr.success('Property added successfully.');
						eventBus.$emit('onLoaderFinish');
						window.location.replace(process.env.MIX_APP_URL + vm.$selectedSite.slug + '/provider-types/' + vm.selectedGroup.provider_type_id + '/groups/' + vm.selectedGroup.id + '/property/' + response.data.id);

					})
					.catch(function (e) {
						vm.errors.record(e.response.data);
						vm.$toastr.error('Save new property failed.', 'Error');
						eventBus.$emit('onLoaderFinish');
					});
		},
		saveProperty: function () {
			let vm = this;

			eventBus.$emit('onLoaderStart');
			vm.generateSelectedPropertyData();

			axios.put('/api/' + vm.$selectedSite.slug + '/provider-types/' + vm.selectedGroup.provider_type_id + '/groups/' + vm.selectedGroup.id + '/properties/' + vm.selectedProperty.id, vm.selectedProperty)
					.then(function (response) {
						vm.$toastr.success('Property updated successfully.');
						eventBus.$emit('onLoaderFinish');
					})
					.catch(function (e) {
						vm.errors.record(e.response.data);
						vm.$toastr.error('Property update failed.', 'Error');
						eventBus.$emit('onLoaderFinish');
					});
		},
		showModal: function (param) {
			let vm = this;
			vm.translationModal = true;
			if (param) {
				vm.isParamUpdate = true;
				vm.selectedParam = param;
			}
		},
		saveParam: function () {
			let vm = this;
			vm.selectedParam.key = vm.selectedParam['en'].text_value;
			if (!vm.isParamUpdate) {
				vm.selectedProperty.params.push(_.cloneDeep(vm.selectedParam));
			}
			vm.hideModal();
		},
		hideModal: function () {
			let vm = this;
			vm.translationModal = false;
			vm.isParamUpdate = false;
			vm.clickedLang = vm.defaultLocale;

			vm.selectedParam = {
				value: null,
				en: {
					text_value: null
				}
			};
			for (let i = 0; i < vm.$availableLanguages.length; i++) {
				vm.selectedParam[vm.$availableLanguages[i].locale] = {
					text_value: null
				}
			}
		},
		deleteParam: function(index) {
			let vm = this;
			vm.selectedProperty.params.splice(index, 1);
		}
	},
	computed: {
		errorNameCurrentLanguage: function () {
			return this.clickedLang + '_name';
		},
		errorDescriptionCurrentLanguage: function () {
			return this.clickedLang + '_description';
		},
	}
}
</script>

<style scoped>

</style>
